import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import ActionDropdownMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/actionDropdown/ActionDropdownMAIQ';
import { DataTableContainer } from 'app/components/StyledComponents';
import { EmailTemplatePreviewDialog } from 'app/NewAIAvatarLayout/sharedComponents/EmailTemplatePreviewDialog';
import type { SegmentAttentionContext } from 'app/utils/hilSegmentsAttentionUtils';
import type { useResolveActionsModalMAIQ } from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveActionsModalMAIQ/hooks/useResolveActionsModalMAIQ';

type ResolveModalHookReturn = ReturnType<typeof useResolveActionsModalMAIQ>;

type ResolveActionsModalContentProps = {
  dealerId: string;
  templateIds: string[];
  context: SegmentAttentionContext;
  modal: ResolveModalHookReturn;
  hideContextChips?: boolean;
};

export default function ResolveActionsModalContent({
  dealerId,
  templateIds,
  context,
  modal,
  hideContextChips = false,
}: ResolveActionsModalContentProps) {
  const hasTemplateIds = templateIds.length > 0;
  const hasActiveTemplates = modal.activeTemplateIds.length > 0;


  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      {!hideContextChips ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {context.segment_name ? (
            <Chip size="small" label={`Segment: ${context.segment_name}`} />
          ) : null}
          {context.issue ? (
            <Chip
              size="small"
              label={`Issue: ${context.issue}`}
              variant="outlined"
            />
          ) : null}
          {context.reason_code ? (
            <Chip
              size="small"
              label={`Reason: ${context.reason_code}`}
              variant="outlined"
            />
          ) : null}
        </Stack>
      ) : null}

      {!hasTemplateIds ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No templates are linked to this attention row.
        </Alert>
      ) : !hasActiveTemplates ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          All linked templates have been processed.
        </Alert>
      ) : !modal.emailTemplatesGridType ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <DataTableContainer
          sx={{ height: 'auto', minHeight: 0, flex: 1, overflow: 'hidden' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0,
              minHeight: 40,
              mt: 0.5,
              mb: 1.5,
              pt: 0.5,
            }}
          >
            <ActionDropdownMAIQ
              buttonLabel="Actions"
              color="primary"
              ariaLabel="Resolve actions for templates"
              actions={[
                {
                  id: 'approve',
                  label: modal.actionLoading ? 'Approving...' : 'Approve',
                  onClick: modal.handleBulkApprove,
                  disabled:
                    modal.selectedRowIds.length === 0 || modal.actionLoading,
                  icon: <CheckCircleOutline fontSize="small" />,
                },
                {
                  id: 'reject',
                  label: modal.actionLoading ? 'Rejecting...' : 'Reject',
                  onClick: modal.handleBulkReject,
                  disabled:
                    modal.selectedRowIds.length === 0 || modal.actionLoading,
                  icon: <CancelOutlined fontSize="small" />,
                },
              ]}
            />
          </Box>
          <DataGridMAIQ
            key={`${dealerId}-${context.segment_name}-${templateIds.length}-${modal.gridRefreshKey}`}
            type={modal.emailTemplatesGridType}
            allowedRowIds={modal.activeTemplateIds}
            onCellClick={modal.handleCellClick}
            onMultiSelect={modal.handleMultiSelect}
          />
        </DataTableContainer>
      )}

      {hasTemplateIds ? (
        <Typography variant="caption" color="text.secondary">
          Showing pending-approval templates for this segment that match this
          attention row. Select templates and use Actions to approve or reject in
          bulk. Use the Preview icon in the Action column to view HTML.
        </Typography>
      ) : null}

      <EmailTemplatePreviewDialog
        open={modal.previewOpen}
        onClose={modal.closePreview}
        templateId={modal.previewTemplateId}
        dealerId={dealerId}
      />
    </Box>
  );
}
